import { Component, OnInit, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OpanaiService } from '../../services/openai.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FileuploaderComponent } from '../../shared/fileuploader/fileuploader.component';
import { EnvironmentService } from '../../services/enviroment.service';
import { environment } from '../../services/global-environment';
import { Router, RouterModule } from '@angular/router';

interface TagChange {
  fileId: string;
  filename: string;
  tags: string[];
  azureUrl?: string;
  uploadedAt?: string;
  originalTags: string[]; // 保存原始標籤，以便比較
}

@Component({
  selector: 'app-filebank',
  standalone: true,
  imports: [CommonModule, FileuploaderComponent, FormsModule],
  templateUrl: './filebank.component.html',
  styleUrls: ['./filebank.component.scss'],
})
export class FilebankComponent implements OnInit {

  @Input() vector_store_id: string = '';  // 透過 Input 注入 vector_store_id
  @Output() backToChat: EventEmitter<void> = new EventEmitter<void>();

  filelist: any[] = [];
  filecount = 0;
  havedeletefile = false;
  companyId: string = '';

  // 記錄標籤變更
  changedTags: Map<string, TagChange> = new Map<string, TagChange>();
  hasChangedTags: boolean = false;
  isSubmitting: boolean = false;

  // 批次操作相關
  selectedFileIds: Set<string> = new Set<string>();

  // 批次加入Tag
  batchAddTagsInput: string = '';

  // 批次移除Tag
  batchRemoveTagList: string[] = [];
  batchRemoveSelectedTags: { [tag: string]: boolean } = {};

  //取得 Vector Store ID
  loadingstatus = 'Loading files...';

  existFileList:string[] = [];


  // 搜尋狀態（檔名或 tag）
  searchText: string = '';

  constructor(
    private openAiApiService: OpanaiService,
    private modalService: NgbModal,
    private router: Router, // 注入 Router
    private environmentService: EnvironmentService // 引入環境服務
  ) {
    // 獲取公司ID
    this.companyId = environment.companyid;
  }

  // 確保在用戶離開頁面前保存標籤變更
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasChangedTags) {
      event.preventDefault();
      event.returnValue = '您有尚未保存的標籤變更，確定要離開嗎？';
    }
  }

  // 取得所有出現過的 tags（去重）
  get allTags(): string[] {
    const tagSet = new Set<string>();
    this.filelist.forEach(file => {
      if (Array.isArray(file.tags)) {
        file.tags.forEach((tag: string) => tagSet.add(tag));
      }
    });
    return Array.from(tagSet);
  }

  // 新增 tag
  addTag(file: any, tag: string) {
    tag = tag.trim();
    if (tag && !file.tags.includes(tag)) {
      // 如果文件還沒有被跟踪，就添加到 changedTags 中
      if (!this.changedTags.has(file.id)) {
        this.changedTags.set(file.id, {
          fileId: file.id,
          filename: file.filename,
          tags: [...file.tags],
          azureUrl: file.azureUrl,
          uploadedAt: file.created_at,
          originalTags: [...file.tags]
        });
      }

      file.tags.push(tag);
      this.changedTags.get(file.id)!.tags = [...file.tags];
      this.hasChangedTags = true;
    }
  }

  // 移除 tag
  removeTag(file: any, tag: string) {
    // 如果文件還沒有被跟踪，就添加到 changedTags 中
    if (!this.changedTags.has(file.id)) {
      this.changedTags.set(file.id, {
        fileId: file.id,
        filename: file.filename,
        tags: [...file.tags],
        azureUrl: file.azureUrl,
        uploadedAt: file.created_at,
        originalTags: [...file.tags]
      });
    }

    file.tags = file.tags.filter((t: string) => t !== tag);
    this.changedTags.get(file.id)!.tags = [...file.tags];
    this.hasChangedTags = true;
  }

  // 依 searchText 篩選 filelist（檔名或 tag）
  get filteredFileList(): any[] {
    if (!this.searchText) return this.filelist;
    const filter = this.searchText.toLowerCase();
    return this.filelist.filter(file =>
      (file.filename && file.filename.toLowerCase().includes(filter)) ||
      (Array.isArray(file.tags) && file.tags.some((tag: string) => tag.toLowerCase().includes(filter)))
    );
  }

  ngOnInit() {
    if (!this.vector_store_id || this.vector_store_id.trim() === '') {
      alert('vector_store_id is empty');
      // 若 vector_store_id 為空則導回 lobby 頁面
    } else {
      // 取得檔案列表
      this.getfillist(this.vector_store_id);
      // 獲取檔案額外資訊
      this.getFilesExtraInfo(this.companyId, this.vector_store_id);
    }
  }

  // 獲取檔案額外資訊
  getFilesExtraInfo(companyid: string, vectorStoreId: string) {
    this.openAiApiService.getFilesExtraInfo(companyid, vectorStoreId).subscribe((response) => {
      if (response.success && response.items && response.items.length > 0) {
        // 將獲取的額外資訊與現有檔案列表進行合併
        this.updateFileListWithExtraInfo(response.items);
      }
    });
  }

  // 更新檔案列表，添加額外資訊
  updateFileListWithExtraInfo(extraInfoItems: any[]) {
    // 建立一個 fileid 對應 extraInfo 的映射
    const extraInfoMap = new Map();
    extraInfoItems.forEach(item => {
      extraInfoMap.set(item.fileid, item);
    });

    // 更新現有文件列表
    this.filelist.forEach(file => {
      const extraInfo = extraInfoMap.get(file.id);
      if (extraInfo) {
        // 將額外資訊合併到檔案中
        file.azureUrl = extraInfo.azureUrl;
        // 如果 extraInfo.tags 有值且 file.tags 為空，則使用 extraInfo.tags
        if (extraInfo.tags && extraInfo.tags.length > 0 && (!file.tags || file.tags.length === 0)) {
          file.tags = extraInfo.tags;
        }
      }
    });
  }

  getfillist(vector_store_id: string) {
    this.openAiApiService.getfilelist(vector_store_id).subscribe((response) => {

      // 初始化每個 file 的 tags 屬性
      this.filelist = response.files.map((file: any) => ({
        ...file,
        tags: Array.isArray(file.tags) ? file.tags : []
      }));
      this.filecount = response.count;

      if (this.filecount == 0) {
          this.loadingstatus=  'No files found.'; // 設置顯示文字
      } else {
        this.loadingstatus='';
        // 從 filelist 中提取名稱並存入 existFileList
        this.existFileList = this.filelist.map((file: { filename: string }) => file.filename);

        // 獲取檔案額外資訊（如果列表不為空）
        if (this.filelist.length > 0) {
          this.getFilesExtraInfo(this.companyId, this.vector_store_id);
        }
      }
    });
  }

  // 批次保存標籤變更
  saveTagChanges() {
    if (this.changedTags.size === 0) {
      return;
    }

    this.isSubmitting = true;
    const savePromises: Promise<any>[] = [];

    // 將 Map 轉換為數組以方便迭代
    const changes = Array.from(this.changedTags.values());

    // 與原始標籤比較，僅保存實際更改的標籤
    const actualChanges = changes.filter(change =>
      JSON.stringify(change.tags.sort()) !== JSON.stringify(change.originalTags.sort())
    );

    if (actualChanges.length === 0) {
      this.isSubmitting = false;
      this.hasChangedTags = false;
      return;
    }

    // 為每個更改的標籤創建一個保存 Promise
    actualChanges.forEach(change => {
      savePromises.push(
        new Promise<any>((resolve, reject) => {
          this.openAiApiService.saveTag(
            this.companyId,
            this.vector_store_id,
            change.fileId,
            change.filename,
            change.azureUrl || null,
            change.tags,
            change.uploadedAt
          ).subscribe({
            next: (response) => resolve(response),
            error: (error) => reject(error)
          });
        })
      );
    });

    // 等待所有保存完成
    Promise.all(savePromises)
      .then(() => {
        // 清空所有標籤變更的追踪
        this.changedTags.clear();
        this.hasChangedTags = false;
        alert('所有標籤變更已保存');
      })
      .catch(error => {
        console.error('保存標籤時發生錯誤:', error);
        alert('保存標籤時發生錯誤，請稍後再試');
      })
      .finally(() => {
        this.isSubmitting = false;
      });
  }

  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const sizeInUnit = bytes / Math.pow(k, i);
    return `${sizeInUnit.toFixed(2)} ${sizes[i]}`;
  }

  deleteFile(fileId: string): void {
    // 找到被刪除的文件
    const file = this.filelist.find((f) => f.id === fileId);
    if (file) {
      // 切換 deleted 狀態
      file.deleted = !file.deleted;
    }

    const deletedCount = this.filelist.filter((f) => f.deleted).length;

    this.havedeletefile = deletedCount > 0;
  }

  showdeleteconfirm(): void {
    // 顯示 Bootstrap Modal
    this.modalService.open({ ariaLabelledBy: 'modal-basic-title' });
  }

  confirmDelete(): void {
    const deletedCount = this.filelist.filter((f) => f.deleted).length;

    if (deletedCount > 0) {
      for (const file of this.filelist) {
        if (file.deleted) {
          this.DeleteFileByReal(file.id);

          // 如果刪除的文件在標籤變更中，需要移除它
          if (this.changedTags.has(file.id)) {
            this.changedTags.delete(file.id);
          }
        }
      }
    }

    //重新計算檔案個數
    this.filecount = this.filelist.length;

    // 如果沒有更多標籤變更，重置標志
    if (this.changedTags.size === 0) {
      this.hasChangedTags = false;
    }
  }

  DeleteFileByReal(fileId: string): void {
    // 實際刪除文件的邏輯
    this.openAiApiService
      .deletefile(this.vector_store_id, fileId)
      .subscribe((response) => {
        // if (response.status === 'success') {
        //   alert("刪除成功");
        // }
      });

    this.filelist = this.filelist.filter((f) => f.id !== fileId);
  }

  // 開啟外部連結
  openExternalLink(url: string): void {
    window.open(url, '_blank');
  }
  // 檢查是否所有可見檔案都被選取
  isAllSelected(): boolean {
    return this.filteredFileList.length > 0 &&
      this.filteredFileList.every(file => this.selectedFileIds.has(file.id));
  }

  // 切換全選/全不選
  toggleSelectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.filteredFileList.forEach(file => this.selectedFileIds.add(file.id));
    } else {
      this.filteredFileList.forEach(file => this.selectedFileIds.delete(file.id));
    }
  }

  // 切換單一檔案選取
  toggleFileSelection(fileId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.selectedFileIds.add(fileId);
    } else {
      this.selectedFileIds.delete(fileId);
    }
  }

  // --- 批次加入Tag Modal ---
  // 解析輸入並批次加入
  confirmBatchAddTag(): void {
    if (!this.batchAddTagsInput.trim()) return;
    // 支援逗號、空白、換行分隔
    const tags = this.batchAddTagsInput
      .split(/[\s,，\n]+/)
      .map(t => t.trim())
      .filter(t => !!t);
    if (tags.length === 0) return;
    this.filteredFileList.forEach(file => {
      if (this.selectedFileIds.has(file.id)) {
        tags.forEach(tag => {
          if (!file.tags.includes(tag)) {
            this.addTag(file, tag);
          }
        });
      }
    });
    this.batchAddTagsInput = '';
  }

  // --- 批次移除Tag Modal ---
  // 準備移除Tag列表（distinct）
  prepareBatchRemoveTagList(): void {
    const tagSet = new Set<string>();
    this.filteredFileList.forEach(file => {
      if (this.selectedFileIds.has(file.id) && Array.isArray(file.tags)) {
        file.tags.forEach((tag: string) => tagSet.add(tag));
      }
    });
    this.batchRemoveTagList = Array.from(tagSet);
    this.batchRemoveSelectedTags = {};
    this.batchRemoveTagList.forEach(tag => {
      this.batchRemoveSelectedTags[tag] = false;
    });
  }

  // 是否有選擇要移除的Tag
  hasBatchRemoveSelectedTags(): boolean {
    return Object.values(this.batchRemoveSelectedTags).some(v => v);
  }

  // 確認批次移除
  confirmBatchRemoveTag(): void {
    const tagsToRemove = Object.keys(this.batchRemoveSelectedTags).filter(tag => this.batchRemoveSelectedTags[tag]);
    if (tagsToRemove.length === 0) return;
    this.filteredFileList.forEach(file => {
      if (this.selectedFileIds.has(file.id)) {
        tagsToRemove.forEach(tag => {
          if (file.tags.includes(tag)) {
            this.removeTag(file, tag);
          }
        });
      }
    });
    // 清空選擇
    this.batchRemoveTagList = [];
    this.batchRemoveSelectedTags = {};
  }
}
